import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, query, orderBy, limit, where } from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { app, db, storage } from '../../firebase';
import ProtectedRoute from '../../components/ProtectedRoute';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ImageUploader from '../../components/ImageUploader';
import FormattedNewsText from '../../components/FormattedNewsText';
import { buildAbsoluteNotificationLink } from '../../services/notificationService';
import {
  buildChangeDescription,
  formatAdminDateTime,
  recordAdminUpdate
} from '../../utils/adminUpdates';

const PUBLIC_PAGES = [
  { label: 'Home', path: '/' },
  { label: 'Shows', path: '/shows' },
  { label: 'Wrestlers', path: '/wrestlers' },
  { label: 'Fazioni', path: '/factions' },
  { label: 'Classifica', path: '/ranking' },
  { label: 'Regole', path: '/rules' },
  { label: 'Statistiche', path: '/stats' },
];

const NEWS_NOTIFICATION_BODY_MAX_LENGTH = 180;

const buildNewsNotificationBody = (description) => {
  const firstLine = String(description || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .find(Boolean) || '';

  if (firstLine.length <= NEWS_NOTIFICATION_BODY_MAX_LENGTH) return firstLine;

  return `${firstLine.slice(0, NEWS_NOTIFICATION_BODY_MAX_LENGTH - 3).trimEnd()}...`;
};

export default function AddNews() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNews, setEditingNews] = useState(null);
  const [editingOriginalImageUrl, setEditingOriginalImageUrl] = useState('');
  const [message, setMessage] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showCreateFormManual, setshowCreateFormManual] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const descriptionRef = useRef(null);

  const [form, setForm] = useState({
    titolo: '',
    descrizione: '',
    link: '',
    tempoFino: '',
    imageUrl: '',
  });

  const [imageInputMode, setImageInputMode] = useState('upload');
  const [linkType, setLinkType] = useState('none');
  const [selectedPage, setSelectedPage] = useState('');
  const [sendPushWithNews, setSendPushWithNews] = useState(true);
  const [sendingManualNotification, setSendingManualNotification] = useState(false);
  const [manualNotification, setManualNotification] = useState({
    title: '',
    body: '',
    link: '/news',
  });
  const [latestShowNotificationPreset, setLatestShowNotificationPreset] = useState(null);
  const [loadingLatestShowPreset, setLoadingLatestShowPreset] = useState(false);

  const loadLatestShowNotificationPreset = async () => {
    setLoadingLatestShowPreset(true);
    try {
      const showsQuery = query(
        collection(db, 'shows'),
        where('data', '<=', Timestamp.fromDate(new Date())),
        orderBy('data', 'desc'),
        limit(1)
      );
      const showsSnap = await getDocs(showsQuery);
      const latestShowDoc = showsSnap.docs[0];

      if (!latestShowDoc) {
        setLatestShowNotificationPreset(null);
        return;
      }

      const latestShow = latestShowDoc.data();
      const showName = latestShow.nome || latestShow.name || 'show';
      setLatestShowNotificationPreset({
        showId: latestShowDoc.id,
        showName,
        title: `Risultati ${showName} adesso online!`,
        body: `Clicca per vedere tutti i bonus e malus dell'ultima puntata di ${showName}`,
        link: `/shows/${latestShowDoc.id}`,
      });
    } catch (error) {
      console.warn('Ultimo show per preset notifica non caricato:', error);
      setLatestShowNotificationPreset(null);
    } finally {
      setLoadingLatestShowPreset(false);
    }
  };

  useEffect(() => {
    loadNews();
    loadLatestShowNotificationPreset();
  }, []);

  const isManagedNewsImage = useCallback((imageUrl) => {
    if (!imageUrl) return false;

    const normalizedUrl = imageUrl.toLowerCase();
    const bucketName = storage.app.options.storageBucket?.toLowerCase() || '';

    return (
      normalizedUrl.startsWith('gs://') ||
      normalizedUrl.includes('%2fnews%2f') ||
      normalizedUrl.includes('/news/') ||
      (bucketName && normalizedUrl.includes(bucketName))
    );
  }, []);

  const deleteStoredNewsImage = useCallback(async (imageUrl) => {
    if (!isManagedNewsImage(imageUrl)) return;

    try {
      await deleteObject(ref(storage, imageUrl));
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        return;
      }
      throw error;
    }
  }, [isManagedNewsImage]);

  const formatDateTimeForInput = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toISOString().slice(0, 16);
  };

  const formatDateForDisplay = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const loadNews = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'news'));
      const newsList = snap.docs.map(entry => ({ id: entry.id, ...entry.data() }));
      newsList.sort((a, b) => (b.dataInserimento?.seconds || 0) - (a.dataInserimento?.seconds || 0));
      setNews(newsList);
      setMessage('');
    } catch (err) {
      console.error('Errore caricamento news:', err);
      setMessage('Errore caricamento news');
    } finally {
      setLoading(false);
    }
  };

  const sendPushNotification = useCallback(async ({ title, body, link }) => {
    const functions = getFunctions(app);
    const sendNotification = httpsCallable(functions, 'sendNotification');

    return sendNotification({
      title: title.trim(),
      body: body.trim(),
      link: buildAbsoluteNotificationLink(link),
    });
  }, []);

  const resetManualNotification = () => {
    setManualNotification({
      title: '',
      body: '',
      link: '/news',
    });
  };

  const applyLatestShowNotificationPreset = () => {
    if (!latestShowNotificationPreset) return;

    setManualNotification({
      title: latestShowNotificationPreset.title,
      body: latestShowNotificationPreset.body,
      link: latestShowNotificationPreset.link,
    });
    setshowCreateFormManual(true);
  };

  const handleManualNotificationToggle = () => {
    if (showCreateFormManual) {
      setshowCreateFormManual(false);
      return;
    }

    setshowCreateFormManual(true);

    const hasManualContent =
      manualNotification.title.trim() ||
      manualNotification.body.trim() ||
      (manualNotification.link.trim() && manualNotification.link.trim() !== '/news');

    if (
      latestShowNotificationPreset &&
      !hasManualContent &&
      window.confirm(`Vuoi creare una notifica per l'ultimo show (${latestShowNotificationPreset.showName})?`)
    ) {
      applyLatestShowNotificationPreset();
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem.id);
    setShowCreateForm(true);
    setshowCreateFormManual(true);
    setSendPushWithNews(false);

    let linkTypeValue = 'none';
    let selectedPageValue = '';
    let linkValue = newsItem.link || '';

    if (newsItem.link && typeof newsItem.link === 'string') {
      if (newsItem.link.startsWith('/')) {
        const isPublicPage = PUBLIC_PAGES.some(page => page.path === newsItem.link);
        if (isPublicPage) {
          linkTypeValue = 'page';
          selectedPageValue = newsItem.link;
          linkValue = '';
        } else {
          linkTypeValue = 'custom';
        }
      } else {
        linkTypeValue = 'external';
      }
    }

    setLinkType(linkTypeValue);
    setSelectedPage(selectedPageValue);
    setImageInputMode(
      newsItem.imageUrl && !isManagedNewsImage(newsItem.imageUrl) ? 'url' : 'upload'
    );

    setForm({
      titolo: newsItem.titolo || '',
      descrizione: newsItem.descrizione || '',
      link: linkValue,
      tempoFino: formatDateTimeForInput(newsItem.tempoFino),
      imageUrl: newsItem.imageUrl || '',
    });
    setEditingOriginalImageUrl(newsItem.imageUrl || '');
  };

  const resetForm = () => {
    setForm({ titolo: '', descrizione: '', link: '', tempoFino: '', imageUrl: '' });
    setImageInputMode('upload');
    setLinkType('none');
    setSelectedPage('');
    setSendPushWithNews(true);
    setEditingNews(null);
    setEditingOriginalImageUrl('');
    setShowCreateForm(false);
    setshowCreateFormManual(false);
  };

  const applyDescriptionFormat = (marker, fallbackText) => {
    const textarea = descriptionRef.current;
    const currentDescription = form.descrizione || '';

    if (!textarea) {
      const nextDescription = `${currentDescription}${marker}${fallbackText}${marker}`;
      setForm(prev => ({ ...prev, descrizione: nextDescription }));
      return;
    }

    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const selectedText = currentDescription.slice(selectionStart, selectionEnd);
    const formattedText = `${marker}${selectedText || fallbackText}${marker}`;
    const nextDescription = [
      currentDescription.slice(0, selectionStart),
      formattedText,
      currentDescription.slice(selectionEnd),
    ].join('');

    setForm(prev => ({ ...prev, descrizione: nextDescription }));

    window.requestAnimationFrame(() => {
      textarea.focus();
      const nextSelectionStart = selectionStart + marker.length;
      const nextSelectionEnd = nextSelectionStart + (selectedText || fallbackText).length;
      textarea.setSelectionRange(nextSelectionStart, nextSelectionEnd);
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      if (!form.tempoFino) {
        setMessage('Data e ora di scadenza richieste');
        return;
      }

      const expiryDate = new Date(form.tempoFino);
      if (expiryDate < new Date()) {
        setMessage('La data di scadenza non puo essere nel passato');
        return;
      }

      let resolvedLink = '';
      switch (linkType) {
        case 'page':
          resolvedLink = selectedPage;
          break;
        case 'custom':
        case 'external':
          resolvedLink = form.link.trim();
          break;
        default:
          resolvedLink = '';
      }

      const imageUrl = form.imageUrl.trim();
      const dataToSave = {
        titolo: form.titolo.trim(),
        descrizione: form.descrizione.trim(),
        link: resolvedLink,
        tempoFino: Timestamp.fromDate(expiryDate),
        imageUrl,
      };

      if (editingNews) {
        const previousNews = news.find(item => item.id === editingNews);
        await updateDoc(doc(db, 'news', editingNews), dataToSave);
        await recordAdminUpdate({
          type: 'News',
          action: 'Modifica',
          title: dataToSave.titolo,
          description: buildChangeDescription('News aggiornata', previousNews, dataToSave, [
            { key: 'titolo', label: 'titolo' },
            { key: 'descrizione', label: 'descrizione' },
            { key: 'link', label: 'link' },
            { key: 'tempoFino', label: 'scadenza', format: formatAdminDateTime },
            { key: 'imageUrl', label: 'immagine', renderChange: () => 'immagine aggiornata' }
          ]),
          entityType: 'news',
          entityId: editingNews
        });
        if (editingOriginalImageUrl && editingOriginalImageUrl !== imageUrl) {
          try {
            await deleteStoredNewsImage(editingOriginalImageUrl);
          } catch (cleanupError) {
            console.warn('Immagine precedente news non rimossa:', cleanupError);
          }
        }
        setMessage('News aggiornata con successo');
      } else {
        dataToSave.dataInserimento = Timestamp.fromDate(new Date());
        const newsRef = await addDoc(collection(db, 'news'), dataToSave);
        await recordAdminUpdate({
          type: 'News',
          action: 'Pubblicazione',
          title: dataToSave.titolo,
          description: dataToSave.descrizione,
          entityType: 'news',
          entityId: newsRef.id
        });
        setMessage('News aggiunta con successo');

        if (sendPushWithNews) {
          try {
            await sendPushNotification({
              title: dataToSave.titolo,
              body: buildNewsNotificationBody(dataToSave.descrizione),
              link: resolvedLink || '/news',
            });
          } catch (notifError) {
            console.warn('Notifica news non inviata:', notifError);
            setMessage('News pubblicata, ma la notifica push non e stata inviata');
          }
        }
      }

      resetForm();
      loadNews();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error('Errore salvataggio:', err);
      setMessage('Errore salvataggio news');
    }
  };

  const handleSendManualNotification = async (e) => {
    e.preventDefault();

    const title = manualNotification.title.trim();
    const body = manualNotification.body.trim();
    const link = manualNotification.link.trim();

    if (!title || !body) {
      setMessage('Titolo e testo della notifica sono obbligatori');
      return;
    }

    setSendingManualNotification(true);

    try {
      await sendPushNotification({
        title,
        body,
        link: link || '/news',
      });

      await recordAdminUpdate({
        type: 'Notifiche',
        action: 'Invio',
        title,
        description: `Push manuale inviata: ${title}`,
        entityType: 'notification',
        entityId: title.toLowerCase().replace(/\s+/g, '-').slice(0, 40) || 'manual-push'
      });

      setMessage('Notifica inviata con successo');
      resetManualNotification();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Errore invio notifica manuale:', error);
      setMessage('Errore invio notifica');
    } finally {
      setSendingManualNotification(false);
    }
  };

  const handleDelete = async (newsItem) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa news?')) return;
    try {
      await deleteDoc(doc(db, 'news', newsItem.id));
      await recordAdminUpdate({
        type: 'News',
        action: 'Eliminazione',
        title: newsItem.titolo || newsItem.id,
        description: `News eliminata: ${newsItem.titolo || newsItem.id}`,
        entityType: 'news',
        entityId: newsItem.id
      });

      let imageDeleteWarning = false;
      if (newsItem.imageUrl) {
        try {
          await deleteStoredNewsImage(newsItem.imageUrl);
        } catch (cleanupError) {
          imageDeleteWarning = true;
          console.warn('Impossibile eliminare l\'immagine associata alla news:', cleanupError);
        }
      }

      setMessage(
        imageDeleteWarning
          ? 'News eliminata, ma non sono riuscito a cancellare l\'immagine dallo storage'
          : 'News eliminata con successo'
      );
      loadNews();
    } catch (err) {
      console.error('Errore eliminazione:', err);
      setMessage('Errore eliminazione news');
    }
  };

  const renderLink = (link) => {
    if (!link) return '-';
    if (link.startsWith('/')) {
      return <span className="text-primary" title={link}><i className="bi bi-link-45deg me-1"></i>Link interno</span>;
    }
    return <a href={link} target="_blank" rel="noopener noreferrer" className="text-decoration-none" title={link}><i className="bi bi-box-arrow-up-right me-1"></i>Link esterno</a>;
  };

  const isExpired = (timestamp) => {
    if (!timestamp) return false;
    const expiryDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return expiryDate < new Date();
  };

  const filteredNews = news.filter((item) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return [item.titolo, item.descrizione, item.link]
      .filter(Boolean)
      .some(value => value.toLowerCase().includes(term));
  });

  const expiredCount = news.filter(item => isExpired(item.tempoFino)).length;

  return (
    <ProtectedRoute>
      <div className="container admin-page py-4">
        <div className="admin-page-heading">
          <div>
            <h1 className="fw-bold">Gestione News</h1>
          </div>
        </div>

        {message && (
          <div className={`alert ${message.toLowerCase().includes('errore') ? 'alert-danger' : 'alert-success'} py-2 mb-4`}>
            {message}
          </div>
        )}

        <div className="admin-stats-grid mb-4">
          <div className="admin-stat-card">
            <span>Attive</span>
            <strong className="text-money">{news.length - expiredCount}</strong>
          </div>
          <div className="admin-stat-card">
            <span>Scadute</span>
            <strong className="text-danger">{expiredCount}</strong>
          </div>
        </div>

        <div className="row g-4">
          <div className="col-lg-5">
            <div className="card shadow-sm">
              <div className="card-header bg-primary text-white py-3 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">
                  <i className={`bi ${editingNews ? 'bi-pencil-square' : 'bi-plus-circle'} me-2`}></i>
                  {editingNews ? 'Modifica News' : 'Nuova News'}
                </h5>
                {!editingNews && (
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={() => setShowCreateForm(prev => !prev)}
                  >
                    <i className={`bi ${showCreateForm ? 'bi-dash-circle' : 'bi-plus-circle'} me-1`}></i>
                    {showCreateForm ? 'Chiudi' : 'Apri'}
                  </button>
                )}
              </div>

              {(editingNews || showCreateForm) && (
                <div className="card-body">
                  <form onSubmit={handleSave}>
                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-semibold">Titolo *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.titolo}
                        onChange={e => setForm({ ...form, titolo: e.target.value })}
                        required
                        maxLength={100}
                      />
                    </div>

                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-semibold">Descrizione *</label>
                      <div className="news-format-toolbar btn-group mb-2" role="group" aria-label="Formattazione descrizione">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => applyDescriptionFormat('**', 'testo in grassetto')}
                          title="Grassetto"
                          aria-label="Grassetto"
                        >
                          <i className="bi bi-type-bold"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => applyDescriptionFormat('*', 'testo in corsivo')}
                          title="Corsivo"
                          aria-label="Corsivo"
                        >
                          <i className="bi bi-type-italic"></i>
                        </button>
                      </div>
                      <textarea
                        ref={descriptionRef}
                        className="form-control"
                        rows="6"
                        value={form.descrizione}
                        onChange={e => setForm({ ...form, descrizione: e.target.value })}
                        required
                      />
                      {form.descrizione.trim() && (
                        <div className="news-description-preview mt-3">
                          <div className="form-label fw-semibold mb-2">Anteprima</div>
                          <FormattedNewsText
                            text={form.descrizione}
                            className="news-rich-text news-description-preview__body"
                          />
                        </div>
                      )}
                    </div>

                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-semibold d-block">Immagine news</label>
                      <div className="btn-group mb-3 w-100" role="group" aria-label="Modalita immagine news">
                        <button
                          type="button"
                          className={`btn ${imageInputMode === 'upload' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setImageInputMode('upload')}
                        >
                          <i className="bi bi-file-earmark-image me-1"></i>
                          File
                        </button>
                        <button
                          type="button"
                          className={`btn ${imageInputMode === 'url' ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => setImageInputMode('url')}
                        >
                          <i className="bi bi-link-45deg me-1"></i>
                          URL
                        </button>
                      </div>

                      {imageInputMode === 'upload' ? (
                        <>
                          <ImageUploader
                            currentImageUrl={form.imageUrl}
                            onUploadSuccess={(url) => setForm(prev => ({ ...prev, imageUrl: url }))}
                            label="Carica da file"
                            storagePath="news"
                            buttonText={form.imageUrl ? 'Sostituisci immagine' : 'Carica immagine'}
                          />
                          <div className="form-text mt-2">
                            Il file viene salvato nella cartella `news` dello storage.
                          </div>
                        </>
                      ) : (
                        <>
                          <input
                            type="url"
                            className="form-control"
                            placeholder="https://esempio.com/immagine.jpg"
                            value={form.imageUrl}
                            onChange={e => setForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                          />
                          <div className="form-text mt-2">
                            Usa un URL pubblico se non vuoi caricare il file nello storage.
                          </div>
                          {form.imageUrl && (
                            <div className="mt-3">
                              <img
                                src={form.imageUrl}
                                alt="Preview news"
                                className="img-fluid rounded"
                                style={{ maxHeight: '160px', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </>
                      )}

                      {form.imageUrl && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm mt-2"
                          onClick={() => setForm(prev => ({ ...prev, imageUrl: '' }))}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Rimuovi immagine
                        </button>
                      )}
                    </div>

                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-semibold">Configurazione Link</label>
                      <select className="form-select mb-2" value={linkType} onChange={e => setLinkType(e.target.value)}>
                        <option value="none">Nessun link</option>
                        <option value="page">Pagina pubblica del sito</option>
                        <option value="custom">Link personalizzato interno</option>
                        <option value="external">URL esterno</option>
                      </select>

                      {linkType === 'page' && (
                        <select className="form-select" value={selectedPage} onChange={e => setSelectedPage(e.target.value)} required>
                          <option value="">Seleziona una pagina...</option>
                          {PUBLIC_PAGES.map(page => <option key={page.path} value={page.path}>{page.label} - {page.path}</option>)}
                        </select>
                      )}
                      {linkType === 'custom' && (
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Es: /show/123, /wrestler/abc..."
                          value={form.link}
                          onChange={e => setForm({ ...form, link: e.target.value })}
                          required
                        />
                      )}
                      {linkType === 'external' && (
                        <input
                          type="url"
                          className="form-control"
                          placeholder="https://esempio.com/pagina"
                          value={form.link}
                          onChange={e => setForm({ ...form, link: e.target.value })}
                          pattern="https?://.+"
                        />
                      )}
                    </div>

                    <div className="admin-panel-soft mb-4">
                      <label className="form-label fw-semibold">Mostra fino al *</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={form.tempoFino}
                        onChange={e => setForm({ ...form, tempoFino: e.target.value })}
                        required
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>

                    {!editingNews && (
                      <div className="admin-panel-soft mb-4">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="send-push-with-news"
                            checked={sendPushWithNews}
                            onChange={(e) => setSendPushWithNews(e.target.checked)}
                          />
                          <label className="form-check-label fw-semibold" htmlFor="send-push-with-news">
                            Invia anche una notifica push alla pubblicazione
                          </label>
                        </div>
                        <div className="form-text mt-2">
                          Se attiva, tutti gli utenti con notifiche abilitate riceveranno una push con il titolo della news.
                        </div>
                      </div>
                    )}

                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                      <button type="button" className="btn btn-outline-info" onClick={loadNews}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Ricarica
                      </button>
                      {(editingNews || showCreateForm) && (
                        <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                          <i className="bi bi-x-lg me-1"></i>Annulla
                        </button>
                      )}
                      <button type="submit" className="btn btn-primary">
                        <i className={`bi ${editingNews ? 'bi-check-lg' : 'bi-plus-lg'} me-1`}></i>
                        {editingNews ? 'Aggiorna News' : 'Crea News'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            <div className="card shadow-sm mt-4">
              <div className="card-header bg-primary text-white py-3 d-flex align-items-center justify-content-between">
                <h5 className="mb-0">
                  <i className="bi bi-megaphone-fill me-2"></i>
                  Notifica manuale
                </h5>
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={handleManualNotificationToggle}
                  >
                    <i className={`bi ${showCreateFormManual ? 'bi-dash-circle' : 'bi-plus-circle'} me-1`}></i>
                    {showCreateFormManual ? 'Chiudi' : 'Apri'}
                  </button>
              </div>
              {showCreateFormManual && (
                <div className="card-body">
                  <form onSubmit={handleSendManualNotification}>
                    {(latestShowNotificationPreset || loadingLatestShowPreset) && (
                      <div className="admin-panel-soft mb-3">
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                          <div>
                            <div className="fw-semibold">
                              <i className="bi bi-stars me-1"></i>
                              Preset ultimo show
                            </div>
                            <small className="text-muted">
                              {loadingLatestShowPreset
                                ? 'Caricamento ultimo show...'
                                : `Prepara la notifica risultati per ${latestShowNotificationPreset.showName}`}
                            </small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={applyLatestShowNotificationPreset}
                            disabled={!latestShowNotificationPreset || loadingLatestShowPreset}
                          >
                            <i className="bi bi-magic me-1"></i>
                            Usa preset
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-semibold">Titolo notifica *</label>
                      <input
                        type="text"
                        className="form-control"
                        maxLength={80}
                        placeholder="Es: Risultati NXT caricati"
                        value={manualNotification.title}
                        onChange={(e) => setManualNotification(prev => ({ ...prev, title: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="admin-panel-soft mb-3">
                      <label className="form-label fw-semibold">Testo notifica *</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        maxLength={160}
                        placeholder="Scrivi il messaggio breve che gli utenti vedranno nella push"
                        value={manualNotification.body}
                        onChange={(e) => setManualNotification(prev => ({ ...prev, body: e.target.value }))}
                        required
                      />
                    </div>

                    <div className="admin-panel-soft mb-4">
                      <label className="form-label fw-semibold">Link apertura</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="/news oppure https://..."
                        value={manualNotification.link}
                        onChange={(e) => setManualNotification(prev => ({ ...prev, link: e.target.value }))}
                      />
                      <div className="form-text text-brand-muted mt-2">
                        Carica un link senza la parte <code>https://fantawrestling.web.app</code>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={resetManualNotification}
                        disabled={sendingManualNotification}
                      >
                        <i className="bi bi-arrow-counterclockwise me-1"></i>
                        Reset
                      </button>
                      <button
                        type="submit"
                        className="btn btn-dark"
                        disabled={sendingManualNotification}
                      >
                        {sendingManualNotification ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Invio...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-send-fill me-1"></i>
                            Invia notifica
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}

            </div>
          </div>

          <div className="col-lg-7">
            <div className="card shadow-sm admin-list-card">
              <div className="card-header bg-light py-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                  <h5 className="mb-0">
                    <i className="bi bi-list-ul me-2"></i>
                    Lista News ({filteredNews.length})
                  </h5>
                  <div className="input-group" style={{ maxWidth: '320px' }}>
                    <span className="input-group-text bg-white">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Cerca news..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="card-body">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Caricamento...</span>
                    </div>
                  </div>
                ) : filteredNews.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-newspaper fs-1 mb-2 d-block"></i>
                    <p className="mb-0">Nessuna news trovata</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Immagine</th>
                          <th>Titolo</th>
                          <th className="d-none d-md-table-cell">Descrizione</th>
                          <th>Link</th>
                          <th>Scadenza</th>
                          <th>Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredNews.map(item => (
                          <tr key={item.id} className={isExpired(item.tempoFino) ? 'table-danger' : ''}>
                            <td style={{ width: '60px' }}>
                              {item.imageUrl ? (
                                <img src={item.imageUrl} alt={item.titolo} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px' }} onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : (
                                <div className="bg-light rounded" style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><i className="bi bi-image text-muted"></i></div>
                              )}
                            </td>
                            <td className="fw-semibold">
                              <div className="text-truncate" style={{ maxWidth: '150px' }}>
                                {item.titolo}
                                {isExpired(item.tempoFino) && (
                                  <span className="badge bg-danger ms-2">SCADUTA</span>
                                )}
                              </div>
                            </td>
                            <td className="d-none d-md-table-cell">
                              <FormattedNewsText
                                text={item.descrizione}
                                singleLine
                                className="text-truncate news-rich-text"
                                style={{ maxWidth: '200px' }}
                              />
                            </td>
                            <td>{renderLink(item.link)}</td>
                            <td>
                              <span className={`badge ${isExpired(item.tempoFino) ? 'bg-danger' : 'bg-success'}`}>
                                {formatDateForDisplay(item.tempoFino)}
                              </span>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <button className="btn btn-sm btn-outline-warning" onClick={() => handleEdit(item)} title="Modifica"><i className="bi bi-pencil"></i></button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(item)} title="Elimina"><i className="bi bi-trash"></i></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProtectedRoute>
  );
}
